// src/pages/AdminLogin.jsx - SEPARATE admin authentication page
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight,
  Shield,
  Settings,
  BarChart3,
  Users
} from "lucide-react";

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (formData.email && formData.password) {
        await login(formData.email, formData.password);
        navigate('/dashboard');
      } else {
        alert('Please enter both email and password');
      }
    } catch (error) {
      if (error?.code === 'ACCOUNT_PENDING_APPROVAL') {
        navigate('/pending-approval');
      } else if (error?.code === 'AUTH_CONFIGURATION_ERROR') {
        alert('Admin authentication is temporarily unavailable. Please contact the system administrator.');
      } else {
        alert(error.message || 'Invalid admin credentials');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const adminFeatures = [
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description: "Monitor network performance and revenue metrics"
    },
    {
      icon: Users,
      title: "User Management",
      description: "Manage advertisers, affiliates, and user accounts"
    },
    {
      icon: Settings,
      title: "System Configuration",
      description: "Configure platform settings and integrations"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Side - Admin Branding */}
          <div className="text-center lg:text-left">
            {/* Admin Logo */}
            <div className="inline-flex items-center space-x-3 mb-8">
              <div className="p-3 bg-gradient-to-r from-red-600 to-red-700 rounded-xl">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <div>
                <span className="text-2xl font-bold text-slate-100">SpaceJet</span>
                <span className="text-2xl font-light text-red-400 ml-1">Admin</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-6 leading-tight">
              Administrative
              <span className="block text-red-400 font-light">
                Control Center
              </span>
            </h1>

            <p className="text-xl text-slate-400 mb-12 max-w-lg mx-auto lg:mx-0">
              Secure access to SpaceJet Network's administrative dashboard and management tools.
            </p>

            {/* Admin Features */}
            <div className="space-y-6">
              {adminFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="flex-shrink-0 p-2 bg-gradient-to-r from-red-700 to-red-800 rounded-lg">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold text-slate-100">{feature.title}</h3>
                      <p className="text-slate-400">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Side - Admin Login Form */}
          <div className="w-full max-w-md mx-auto">
            <div className="admin-glass rounded-2xl p-8 border border-red-700/30">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-600 to-red-700 rounded-full mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-slate-100 mb-2">Admin Access</h2>
                <p className="text-slate-400">Enter your administrative credentials</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Admin Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="growth@spacejetmedia.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Admin Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-12 py-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Enter admin password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-slate-400 hover:text-slate-300" />
                      ) : (
                        <Eye className="h-5 w-5 text-slate-400 hover:text-slate-300" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-admin"
                      name="remember-admin"
                      type="checkbox"
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-slate-600 rounded bg-slate-700"
                    />
                    <label htmlFor="remember-admin" className="ml-2 block text-sm text-slate-300">
                      Keep me signed in
                    </label>
                  </div>

                  <div className="text-sm text-slate-500">Contact an existing admin for access.</div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="group w-full px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 disabled:from-red-800 disabled:to-red-900 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center border border-red-500/50"
                >
                  {isLoading ? 'Authenticating...' : 'Access Admin Panel'}
                  {!isLoading && <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-700">
                <p className="text-center text-sm text-slate-400">
                  This is a secure administrative area. Unauthorized access is prohibited.
                </p>
              </div>
            </div>

            {/* Security Notice */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center text-slate-400 text-sm">
                <Shield className="w-4 h-4 mr-2" />
                Secured with enterprise-grade authentication
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
